import { motion } from 'framer-motion';
import { ArrowRight, Film, Award, Users, ChevronRight, Instagram, Facebook, Twitter, Youtube, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { IMAGES } from '@/assets/images';
import { services, facilities, testimonials } from '@/data/index';
import { ServiceCard, FacilityCard, TestimonialCard } from '@/components/Cards';
import { ROUTE_PATHS } from '@/lib/index';
import { Link } from 'react-router-dom';
export default function Home() {
  return <div className="min-h-screen">
      <section id="hero" className="relative min-h-screen flex items-start justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.BIBIA_V3_TITLED_50ACACF70D8B4B0B84091BF3C16AB021_EZGIF_COM_OPTIMIZE_232}
            alt="Bibia Films - Keshhav Panneriy on Set"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/10 to-background/50" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center mt-[30vh] pt-[250px] pb-[250px]">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }}>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight"></h1>
            <p className="text-xl md:text-2xl lg:text-3xl mb-4 max-w-3xl mx-auto text-[rgb(200,15,15)]">Future-Facing Cinema</p>
            <p className="text-lg md:text-xl mb-12 max-w-2xl mx-auto font-semibold text-[rgb(0,0,0)]">
              International film production and post-production studio founded by Keshhav Panneriy. Creating one-globe narratives that transcend borders, cultures, and languages.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8" asChild>
                <a href="#services">
                  Explore Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8" asChild>
                <Link to={ROUTE_PATHS.CONTACT}>
                  Get in Touch
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1,
        duration: 1
      }}>
          <a href="#about" className="flex flex-col items-center text-muted-foreground hover:text-foreground transition-colors">
            <span className="text-sm mb-2">Scroll to explore</span>
            <ChevronRight className="h-6 w-6 rotate-90 animate-bounce" />
          </a>
        </motion.div>

        <motion.div className="absolute bottom-8 left-8 z-20" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2,
        duration: 1
      }}>
          
        </motion.div>

        <motion.div className="absolute bottom-24 right-8 z-30" initial={{
        opacity: 0,
        x: 20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 1.4,
        duration: 0.8
      }}>
          <div className="flex flex-col gap-3">
            <a href="https://wa.me/917304047935" target="_blank" rel="noopener noreferrer" className="group flex items-center gap-3 text-white hover:text-white transition-all bg-[#25D366]/90 backdrop-blur-md px-4 py-3 rounded-lg border-2 border-white/30 hover:border-white/60 hover:bg-[#25D366] shadow-lg" title="Chat with us on WhatsApp">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold mb-0.5">WhatsApp</p>
                <p className="text-xs opacity-90">Connect instantly</p>
              </div>
            </a>

            
          </div>
        </motion.div>
      </section>

      <section id="about" className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Our Vision</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Cinema is more than entertainment—it is a universal language. At Bibia Films, our purpose is to bridge local stories with international audiences through technology, craft, and vision.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-center p-8 rounded-2xl bg-background border border-border">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Film className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Global Storytelling</h3>
              <p className="text-muted-foreground">
                Creating narratives that resonate across borders, cultures, and languages through shared human emotions.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="text-center p-8 rounded-2xl bg-background border border-border">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Award className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">Artistic Integrity</h3>
              <p className="text-muted-foreground">
                Balancing creative vision with global market sensibilities while maintaining the highest artistic standards.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="text-center p-8 rounded-2xl bg-background border border-border">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Users className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-4">World-Class Talent</h3>
              <p className="text-muted-foreground">
                Bringing together directors, cinematographers, technicians, and artists from across the globe.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      

      

      <section id="testimonials" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Client Testimonials</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Hear from directors, producers, and creatives who have brought their visions to life at Bibia Films.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {testimonials.map((testimonial, index) => <motion.div key={testimonial.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.1
          }}>
                <TestimonialCard testimonial={testimonial} />
              </motion.div>)}
          </div>
        </div>
      </section>

      <section id="cta" className="py-24 bg-card relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.CINEMA_SCREEN_1} alt="Cinema Screen" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-card/80 via-card/90 to-card" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Create Future-Facing Cinema?
            </h2>
            <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">
              Whether you're planning a feature film, commercial production, or international co-production, Bibia Films has the facilities, equipment, and expertise to bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="text-lg px-8" asChild>
                <Link to={ROUTE_PATHS.CONTACT}>
                  Start Your Project
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8" asChild>
                <Link to={ROUTE_PATHS.ABOUT}>
                  Learn More About Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}